import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  output,
  runInInjectionContext,
  signal,
  untracked,
  type WritableSignal,
} from '@angular/core';
import { form, type FieldTree } from '@angular/forms/signals';
import { SignalFormDirective, validateSignalForm } from '@vallift/ngl';
import { FormFieldPreviewControlComponent } from '../form-field-preview-control/form-field-preview-control.component';
import type { BuiltForm } from '../../models/built-form';
import type { PublicFormAuthOptions } from '@core/api/public/public-forms-api.service';
import {
  DEFAULT_PAGE_ID,
  DEFAULT_PRIMARY_LABEL,
  findPage,
  sortedPages,
} from '../../utils/form-pages';
import { previewShellWidthClass } from '../../utils/form-preview-styles';
import { themeAccentHex, themeCssVars } from '../../utils/form-theme-catalog';
import {
  answerFieldTree,
  applyAnswerValidators,
  createAnswerDefaults,
  formStructureKey,
  isAnswerField,
  validateAnswerFieldIds,
  type AnswerValue,
  type RespondentAnswers,
} from '../../utils/respondent-answer-form';
import { mergeDraftAnswers } from '../../utils/serialize-answers-for-api';

export type PublicFormFillContext = {
  workspaceSlug: string;
  formSlug: string;
  auth: PublicFormAuthOptions;
};

type AnswerSession = {
  key: string;
  model: WritableSignal<RespondentAnswers>;
  form: FieldTree<RespondentAnswers>;
};

@Component({
  selector: 'app-form-preview-card',
  standalone: true,
  imports: [NgStyle, FormFieldPreviewControlComponent, SignalFormDirective],
  templateUrl: './form-preview-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block w-full',
  },
})
export class FormPreviewCardComponent {
  private readonly injector = inject(Injector);

  readonly form = input.required<BuiltForm>();
  readonly mode = input<'desktop' | 'mobile'>('desktop');
  readonly enableSubmit = input(false);
  readonly submitting = input(false);
  readonly initialAnswers = input<
    Array<{ fieldId: string; value: unknown }> | null
  >(null);
  readonly publicContext = input<PublicFormFillContext | null>(null);

  readonly submitAnswers = output<RespondentAnswers>();
  readonly answersChanged = output<RespondentAnswers>();

  readonly previewShellWidthClass = previewShellWidthClass;
  readonly themeCssVars = themeCssVars;
  readonly themeAccentHex = themeAccentHex;

  private readonly activePageId = signal<string>(DEFAULT_PAGE_ID);
  readonly answerSession = signal<AnswerSession | null>(null);
  private skipNextAnswersEmit = false;

  readonly orderedPages = computed(() => sortedPages(this.form().pages));

  readonly activePage = computed(() =>
    findPage(this.form(), this.activePageId()),
  );

  readonly fields = computed(() => this.activePage()?.fields ?? []);

  readonly activeIndex = computed(() => {
    const pages = this.orderedPages();
    const id = this.activePage()?.id;
    const index = pages.findIndex((page) => page.id === id);
    return index >= 0 ? index : 0;
  });

  readonly pageCount = computed(() => this.orderedPages().length);

  readonly showPageIndicator = computed(() => this.pageCount() > 1);

  readonly pageIndicator = computed(() => {
    if (!this.showPageIndicator()) {
      return '';
    }
    return `Page ${this.activeIndex() + 1} of ${this.pageCount()}`;
  });

  readonly isFirstPage = computed(() => this.activeIndex() <= 0);

  readonly isLastPage = computed(
    () => this.activeIndex() >= this.pageCount() - 1,
  );

  readonly primaryLabel = computed(
    () =>
      this.activePage()?.cta?.primaryLabel?.trim() || DEFAULT_PRIMARY_LABEL,
  );

  readonly backLabel = computed(
    () => this.activePage()?.cta?.backLabel?.trim() || 'Back',
  );

  constructor() {
    effect(() => {
      const builtForm = this.form();
      const key = formStructureKey(builtForm);
      const draftAnswers = this.initialAnswers();
      untracked(() => {
        const current = this.answerSession();
        if (current?.key === key) {
          return;
        }
        const defaults = createAnswerDefaults(builtForm);
        const initial = draftAnswers?.length
          ? mergeDraftAnswers(defaults, draftAnswers)
          : defaults;
        this.skipNextAnswersEmit = true;
        const model = signal(initial);
        const answersForm = runInInjectionContext(this.injector, () =>
          form(model, (schema) => {
            applyAnswerValidators(schema, builtForm);
          }),
        );
        this.answerSession.set({ key, model, form: answersForm });
      });
    });

    effect(() => {
      const pages = sortedPages(this.form().pages);
      const firstId = pages[0]?.id ?? DEFAULT_PAGE_ID;
      const currentId = this.activePageId();
      if (!pages.some((page) => page.id === currentId)) {
        this.activePageId.set(firstId);
      }
    });

    effect(() => {
      const session = this.answerSession();
      if (!session || !this.enableSubmit()) {
        return;
      }
      const value = session.model();
      if (this.skipNextAnswersEmit) {
        this.skipNextAnswersEmit = false;
        return;
      }
      this.answersChanged.emit(value);
    });
  }

  fieldAnswerControl(fieldId: string): FieldTree<AnswerValue> | undefined {
    const session = this.answerSession();
    if (!session) {
      return undefined;
    }
    return answerFieldTree(session.form, fieldId);
  }

  goBack(): void {
    if (this.isFirstPage()) {
      return;
    }
    const prev = this.orderedPages()[this.activeIndex() - 1];
    if (prev) {
      this.activePageId.set(prev.id);
    }
  }

  onPrimary(): void {
    if (this.submitting()) {
      return;
    }
    const session = this.answerSession();
    if (!session) {
      return;
    }

    if (this.isLastPage()) {
      if (!validateSignalForm(session.form)) {
        return;
      }
      if (this.enableSubmit()) {
        this.submitAnswers.emit(session.form().value());
      }
      return;
    }

    const pageFieldIds = (this.activePage()?.fields ?? [])
      .filter(isAnswerField)
      .map((field) => field.id);
    if (!validateAnswerFieldIds(session.form, pageFieldIds)) {
      return;
    }

    const next = this.orderedPages()[this.activeIndex() + 1];
    if (next) {
      this.activePageId.set(next.id);
    }
  }

  patchAnswers(next: RespondentAnswers): void {
    const session = this.answerSession();
    if (!session) {
      return;
    }
    this.skipNextAnswersEmit = true;
    session.model.set(next);
  }

  currentAnswers(): RespondentAnswers | null {
    return this.answerSession()?.model() ?? null;
  }
}
